import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UsageEvent } from '@heybesafe/shared';
import { decryptUsageEventPayload } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { useFamilyKeys } from '../context/FamilyKeysContext';
import { getAdminInbox, getOrgEvents } from '../api/client';
import { base64UrlToBytes } from '../crypto/base64url';
import { useUsageAnalysis } from './usage/useUsageAnalysis';
import { AnomalyHeadsUpPanel } from './usage/AnomalyHeadsUpPanel';
import { MemberActivityStrip } from './usage/MemberActivityStrip';
import { EventsFeedTable } from './usage/EventsFeedTable';

const PAGE_SIZE = 25;

export default function EventsPage({ initialOrgId }: { initialOrgId?: string } = {}) {
  const { orgs } = useSafe();
  const familyKeys = useFamilyKeys();

  const [events, setEvents]           = useState<UsageEvent[]>([]);
  const [loading, setLoading]         = useState(true);
  const [filterOrgId, setFilterOrgId] = useState(initialOrgId ?? '');
  const [nextToken, setNextToken]     = useState<string | undefined>(undefined);
  const [prevTokens, setPrevTokens]   = useState<string[]>([]);
  const [currentToken, setCurrentToken] = useState<string | undefined>(undefined);
  const [decrypted, setDecrypted] = useState<Map<string, { itemName: string; siteUrl: string } | null>>(new Map());

  const analysis = useUsageAnalysis(filterOrgId);

  const fetchEvents = useCallback(async (token?: string) => {
    setLoading(true);
    try {
      const result = filterOrgId
        ? await getOrgEvents(filterOrgId, { limit: PAGE_SIZE, nextToken: token })
        : await getAdminInbox({ limit: PAGE_SIZE, nextToken: token });

      if (result.ok) {
        setEvents(result.data.events);
        setNextToken(result.data.nextToken);
      }
    } finally {
      setLoading(false);
    }
  }, [filterOrgId]);

  useEffect(() => {
    setCurrentToken(undefined);
    setPrevTokens([]);
    fetchEvents(undefined);
  }, [fetchEvents]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = new Map<string, { itemName: string; siteUrl: string } | null>();
      for (const ev of events) {
        const priv = familyKeys.getKey(ev.orgId);
        const org = orgs.find((o) => o.id === ev.orgId);
        if (!priv || !org?.publicKey) {
          out.set(ev.id, null);
          continue;
        }
        try {
          const pubBytes = base64UrlToBytes(org.publicKey);
          out.set(ev.id, await decryptUsageEventPayload(ev, priv, pubBytes));
        } catch {
          out.set(ev.id, null);
        }
      }
      if (!cancelled) setDecrypted(out);
    })();
    return () => { cancelled = true; };
  }, [events, familyKeys, orgs]);

  function handleNext() {
    if (!nextToken) return;
    setPrevTokens((prev) => [...prev, currentToken ?? '']);
    setCurrentToken(nextToken);
    fetchEvents(nextToken);
  }

  function handlePrev() {
    const prev = [...prevTokens];
    const token = prev.pop() ?? undefined;
    setPrevTokens(prev);
    setCurrentToken(token);
    fetchEvents(token);
  }

  const orgNameById = useMemo(
    () => (id: string) => orgs.find((o) => o.id === id)?.name ?? id,
    [orgs],
  );

  const flagsByEventId = analysis.analysis?.flagsByEventId ?? new Map();
  const anomalies = analysis.analysis?.anomalies ?? [];
  const perMember = analysis.analysis?.perMember ?? [];

  return (
    <div className="px-6 py-6">
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 className="text-xl font-semibold text-hbs-text">Usage Events</h1>
        <div className="flex items-center gap-2">
          {analysis.status === 'loading' && (
            <span className="text-xs text-hbs-text-mute">Analyzing…</span>
          )}
          {!initialOrgId && (
            <select
              value={filterOrgId}
              onChange={(e) => setFilterOrgId(e.target.value)}
              className="bg-hbs-surface border border-hbs-border rounded-hbs-md px-3 py-2 text-sm text-hbs-text focus:outline-none focus:border-hbs-primary transition-colors"
            >
              <option value="">All families</option>
              {orgs.filter((o) => o.role === 'admin').map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div role="status" aria-live="polite" className="sr-only">
        {analysis.status === 'ready' && `Analysis complete — ${anomalies.length} anomalies`}
      </div>

      {analysis.status === 'ready' && <AnomalyHeadsUpPanel anomalies={anomalies} />}

      <MemberActivityStrip members={perMember} />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-hbs-text-mute text-sm">Loading…</div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-hbs-text-mute gap-3">
          <svg className="w-10 h-10 text-hbs-text-mute" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-sm">No events yet.</p>
        </div>
      ) : (
        <>
          <EventsFeedTable
            events={events}
            orgNameById={orgNameById}
            plaintextById={decrypted}
            flagsByEventId={flagsByEventId}
          />

          {(prevTokens.length > 0 || nextToken) && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-hbs-border mt-4">
              <button
            id="events-prev-page-button"
            onClick={handlePrev}
                disabled={prevTokens.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
              <span className="text-xs text-hbs-text-mute">{events.length} events</span>
              <button
            id="events-next-page-button"
            onClick={handleNext}
                disabled={!nextToken}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt rounded-hbs-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
